import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadList, settledList } from '../../utils/loadState'
import { getJurisdictions, getComplianceRules } from '../../api'
import './ComplianceRules.css'

export default function ComplianceRules() {
  const [jurisdictions, setJurisdictions] = useState([])
  const [rules, setRules] = useState([])
  const [selectedJuris, setSelectedJuris] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  // A failed load is an error with a Retry, never "No rules found" (item 201).
  const [jurisError, setJurisError] = useState(null)
  const [rulesError, setRulesError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [j, r] = await Promise.allSettled([getJurisdictions(), getComplianceRules()])
      const ju = settledList(j, 'jurisdictions')
      setJurisdictions(ju.items || [])
      setJurisError(ju.error)
      const ru = settledList(r, 'rules')
      setRules(ru.items || [])
      setRulesError(ru.error)
    } finally { setLoading(false) }
  }

  // The filter is the jurisdiction's CODE (e.g. US-CA), the key /api/compliance/rules and
  // every other compliance endpoint read. It used to send the jurisdiction's id, which the
  // endpoint ignored, so every pick listed every rule.
  async function loadForJurisdiction(jurisdictionCode) {
    setLoading(true)
    const { items, error } = await loadList(() => getComplianceRules({ jurisdiction_code: jurisdictionCode }), 'rules')
    setRules(items || [])
    setRulesError(error)
    setLoading(false)
  }

  // On mount, and whenever the pick changes: no pick (All Jurisdictions) loads every rule
  // again -- it used to keep showing the last jurisdiction's rules.
  useEffect(() => {
    if (!selectedJuris) { load(); return }
    loadForJurisdiction(selectedJuris)
  }, [selectedJuris])

  const filtered = rules.filter(r => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return [r.rule_name, r.rule_code, r.summary, r.rule_type, r.jurisdiction_code]
      .some(v => (v || '').toLowerCase().includes(q))
  })

  const typeColors = { wage: 'var(--green)', tax: 'var(--blue)', benefit: 'var(--purple)', leave: 'var(--amber)', safety: 'var(--red)' }

  return (
    <>
      <TopNav breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Compliance Rules' }]}
        actions={<><Link to="/admin" className="nav-btn">Dashboard</Link></>} />
      <div className="cr-page">
        <div className="cr-header">
          <div><h1 className="cr-title">Compliance Rules</h1><p className="cr-subtitle">{jurisError ? '—' : jurisdictions.length} jurisdictions · {rulesError ? '—' : rules.length} rules</p></div>
          <div className="cr-controls">
            <select className="cr-select" value={selectedJuris} onChange={e => setSelectedJuris(e.target.value)}>
              <option value="">All Jurisdictions</option>
              {jurisdictions.map(j => <option key={j.code} value={j.code}>{j.name} ({j.jurisdiction_type})</option>)}
            </select>
            <input className="cr-search" placeholder="Search rules…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {jurisError && <LoadError what="jurisdictions" error={jurisError} onRetry={load} />}

        {/* Jurisdiction chips */}
        <div className="cr-juris-bar">
          {jurisdictions.filter(j => j.jurisdiction_type === 'federal').map(j => (
            <button key={j.code} className={`cr-juris-chip federal ${selectedJuris === j.code ? 'active' : ''}`} onClick={() => setSelectedJuris(selectedJuris === j.code ? '' : j.code)}>{j.code}</button>
          ))}
          {jurisdictions.filter(j => j.jurisdiction_type === 'state').slice(0, 20).map(j => (
            <button key={j.code} className={`cr-juris-chip ${selectedJuris === j.code ? 'active' : ''}`} onClick={() => setSelectedJuris(selectedJuris === j.code ? '' : j.code)}>{j.subdivision_code || j.code}</button>
          ))}
          {jurisdictions.filter(j => j.jurisdiction_type === 'state').length > 20 && (
            <span className="cr-juris-more">+{jurisdictions.filter(j => j.jurisdiction_type === 'state').length - 20} more</span>
          )}
        </div>

        {loading ? (
          <div className="cr-loading"><div className="cr-spinner" /><p>Loading…</p></div>
        ) : rulesError ? (
          <LoadError what="rules" error={rulesError} onRetry={() => (selectedJuris ? loadForJurisdiction(selectedJuris) : load())} />
        ) : filtered.length > 0 ? (
          <div className="cr-list">
            {filtered.map((r, i) => (
              <div key={i} className="cr-card">
                <span className="cr-card-type" style={{ background: typeColors[r.rule_type] || 'var(--gray-400)' }}>{r.rule_type || 'rule'}</span>
                <div className="cr-card-info">
                  <div className="cr-card-name">{r.rule_name || r.rule_code || 'Unnamed Rule'}</div>
                  <div className="cr-card-desc">{r.summary?.slice(0, 150) || 'No description'}</div>
                  <div className="cr-card-meta">{r.jurisdiction_code || ''} {r.effective_date && `· Effective: ${r.effective_date}`}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="cr-empty">No rules found{selectedJuris ? ' for this jurisdiction' : ''}.</div>
        )}
      </div>
    </>
  )
}
